import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Guards a dirty form against the two exits we can intercept: closing or reloading the tab
// (browser prompt) and clicking an in-app link (our confirm dialog, then navigate). The browser
// back button is not intercepted: with <BrowserRouter> the route has already changed by the time
// a popstate listener runs, and there is no blocker API without a data router.
export function useUnsavedChanges(dirty: boolean, confirmLeave: () => Promise<boolean>) {
  const navigate = useNavigate();
  const confirmRef = useRef(confirmLeave);
  confirmRef.current = confirmLeave;

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome for the prompt to show
    };
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const dest = url.pathname + url.search + url.hash;
      if (dest === window.location.pathname + window.location.search + window.location.hash) return;
      e.preventDefault();
      e.stopPropagation();
      void confirmRef.current().then((ok) => { if (ok) navigate(dest); });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, navigate]);
}
